<script>
  import { mapState } from 'vuex';
  import { worldsSlides } from '@/views/app/dashboard/dashboard/data/worldsSlides';
  import { gamesSlides } from '@/views/app/dashboard/dashboard/data/gamesSlides';
  import { availableGames, sortFastplayGames, sortPvpGames } from '@/data/games';
  import { GameLauncher } from '@/mixins/game-launcher';
  import LoggedOutView from '@/views/app/dashboard/dashboard/views/LoggedOutView.vue';
  import GameCard from '@/components/cards/GameCard.vue';
  import PromoBanners from '@/components/lobby/PromoBanners.vue';
  import router from '@/router';
  import breakpoints from '@/mixins/breakpoints';
  import EventBus from '@/plugins/eventBus';

  export default {
    name: 'HomePageView',

    metaInfo: {
      title: 'Analytic',
    },

    mixins: [GameLauncher, breakpoints],

    components: {
      LoggedOutView,
      GameCard,
      PromoBanners,
    },

    data() {
      return {
        worldsSlides,
        gamesSlides,
        gameTitleLeftmostItem: {
          gameTitleCarousel1: 0,
          gameTitleCarousel2: 0,
        },
        ytTrailer: false,
        showSignupDialog: false,
        emptyColorFill: {
          radial: true,
          colors: [
            {
              color: '#ce952e',
              offset: '50',
              opacity: '0.2',
            },
            {
              color: '#ce952e',
              offset: '50',
              opacity: '0.15',
            },
            {
              color: '#ce952e',
              offset: '70',
              opacity: '0.15',
            },
            {
              color: '#ce952e',
              offset: '70',
              opacity: '0.1',
            },
            {
              color: '#ce952e',
              offset: '90',
              opacity: '0.1',
            },
            {
              color: 'transparent',
              offset: '90',
              opacity: '0.1',
            },
            {
              color: 'transparent',
              offset: '95',
              opacity: '0.1',
            },
            {
              color: 'transparent',
              offset: '95',
              opacity: '0.1',
            },
          ],
        },
        progdata: [
          {
            progress: 50, // required for each circle
            color: '#ce952e', // will overwrite global progress color
          },
        ],
        gradient: {
          radial: false,
          colors: [
            {
              color: '#ce952e',
              offset: 0,
              opacity: '1',
            },
            {
              color: 'black',
              offset: 100,
              opacity: '0.6',
            },
          ],
        },
        carousel: 0,
        carouselBanners: [
          {
            desktop: '/images/banners/hero-banner-1-desktop.webp?v=1',
            mobile: '/images/banners/hero-banner-1-mobile.webp?v=1',
          },
          {
            desktop: '/images/banners/hero-banner-grab-5-mc-desktop.webp?v=1',
            mobile: '/images/banners/hero-banner-grab-5-mc-mobile.webp?v=1',
          },
          {
            desktop: '/images/banners/hero-banner-2-desktop.webp?v=1',
            mobile: '/images/banners/hero-banner-2-mobile.webp?v=1',
          },
          {
            desktop: '/images/banners/hero-banner-4-desktop.webp?v=1',
            mobile: '/images/banners/hero-banner-4-mobile.webp?v=1',
          },
          {
            desktop: '/images/banners/hero-banner-3-desktop.webp?v=1',
            mobile: '/images/banners/hero-banner-3-mobile.webp?v=1',
          },
        ],
        pvpCards: [
          {
            icon: '/images/svgs/play.svg',
            title: 'PLAY FOR FREE',
            body: `Focus on having fun for free on the 1st true Player vs Player game experience. Join the growing community, earn rewards and enjoy big crypto wins!`,
          },
          {
            icon: '/images/svgs/exclusive.svg',
            title: 'EXCLUSIVE GAMES',
            body: `Take your gaming to the next level with our exclusive games such as Max Blast Champs and Sector X from our growing collection of exhilarating games.`,
          },
          {
            icon: '/images/svgs/challenge.svg',
            title: 'CHALLENGE YOUR FRIENDS',
            body: `Invite your friends and enjoy instant action together in your private room! Secure the highest score and win big crypto prizes.`,
          },
        ],
        carouselTimerId: null,
      };
    },

    computed: {
      ...mapState([
        'isOlympusAvailable',
        'isMultiPlinkoLimitedAvailable',
        'isRollOrHoldLimitedAvailable',
        'isEscapeTheCircleLimitedAvailable',
      ]),

      gameCards() {
        return availableGames;
      },

      maxRewardBanner() {
        return this.$vuetify.breakpoint.xsOnly
          ? '/images/banners/payments-mini.webp?v=4'
          : '/images/banners/payments.webp?v=4';
      },

      isTwoCardsBreakpoint() {
        return this.$vuetify.breakpoint.width < 800;
      },

      swiperOptions() {
        return {
          'slides-per-group': this.isTwoCardsBreakpoint ? 2 : 1,
          'slides-per-view': this.isTwoCardsBreakpoint ? 2 : 3,
          'space-between': this.isTwoCardsBreakpoint ? 12 : 24,
          loop: true,
        };
      },

      getCarouselChevronSize() {
        return this.isMobileOrTablet ? '20px' : '28px';
      },

      userInfo() {
        return this.$store.getters.getuser;
      },

      userName() {
        if (!this.userInfo) return '';
        return this.userInfo.firstname || this.userInfo.displayName || '';
      },

      userSignedIn() {
        return !!this.userInfo;
      },

      isLoading() {
        return !this.$store.state.userCheckCompleted;
      },

      /**
       * Returns a filtered array of game cards that belong to the Fast Play category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards with category 'FP'
       */
      getAllFPGameCards() {
        const cards = this.gameCards.filter((card) => card.category === 'FP');
        if (!cards.length) return [];
        return sortFastplayGames(cards);
      },

      /**
       * Returns a filtered array of game cards that belong to the PVP category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards with category 'PVP'
       */
      getAllPVPGameCards() {
        return sortPvpGames(this.gameCards.filter((card) => card.category === 'PVP'));
      },

      /**
       * Returns a filtered array of game cards that belong to the CAF category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards with category 'CAF'
       */
      getAllCAFGameCards() {
        return this.gameCards.filter((card) => card.category === 'CAF');
      },

      /**
       * Returns a filtered array of game cards that belong to the Fish table category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards with category 'Fish table'
       */
      getAllFishTableGameCards() {
        return this.gameCards.filter((card) => card.category === 'FishTable');
      },

      /**
       * Returns a filtered array of game cards that belong to the Game Title category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards with category 'CAF'
       */
      getAllGameTitlesCards() {
        const cards = this.gameCards.filter((card) => card.category === 'gameTitles');
        return [...cards, ...cards];
      },

      /**
       * Returns a filtered array of game cards that belong to the MB category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards with category 'MB'
       */
      getAllMBGameCards() {
        const cards = this.gameCards.filter((card) => card.category === 'MB');
        if (this.isMobileOrTablet) return cards;
        if (cards.length > 2) return cards;
        return [...cards, ...cards, ...cards];
      },

      /**
       * Returns a filtered array of game cards that belong to the Plinko category.
       *
       * @return {{title: String, image: String, category: String}[]} An array of game cards with category 'Plinko'
       */
      getAllPlinkoGameCards() {
        const cards = this.gameCards.filter((card) => card.category === 'Plinko');
        if (this.isMobileOrTablet) return cards;
        if (cards.length > 2) return cards;
        return [...cards, ...cards, ...cards];
      },

      promoBanners() {
        return [
          {
            src: '/images/banners/PVP-promo-banner.webp?v=4',
            alt: 'Player vs Player',
            to: '/playervsplayer',
          },
          {
            src: '/images/banners/RAF-promo-banner.webp?v=4',
            alt: 'Refer a friend',
            to: '/referfriends',
          },
          {
            src: '/images/banners/CAF-promo-banner.webp?v=4',
            alt: 'Challenge friends',
            to: '/challenge-friends',
          },
        ];
      },

      isUserSuspended() {
        return this.$store.getters.isUserSuspended;
      },

      getPromoPackages() {
        return this.$store.getters.getPromoPackages;
      },

      isLoggedInUser() {
        const userExists = !!this.$store.getters.getuser;
        return userExists && !this.isSocialSignUp;
      },

      isSidebarOpened() {
        if (!this.isLoggedInUser) return false;
        const state = this.$store.state.sidebar;
        return this.isMobileOrTablet ? state : !state;
      },
    },

    watch: {
      isOlympusAvailable: {
        immediate: true,
        deep: true,
        handler(olympus) {
          const limitedAccess = this.$route.query['olympus-limited-access'];

          if (limitedAccess && !this.userSignedIn) {
            this.openSignUpDialog();
          }

          if (olympus) {
            if (limitedAccess && this.userSignedIn) {
              this.launchGame(11, 'PL', 'extended', 11);
            }
          }
        },
      },

      isMultiPlinkoLimitedAvailable: {
        handler(isAvailable) {
          if (
            !isAvailable ||
            (!this.$route.query?.['multiplinko-limited-access'] &&
              !this.$route.query?.['buyIn'] &&
              !this.$route.query?.['bankId'])
          )
            return;
          if (this.userSignedIn) {
            this.$store.commit('setPlayerMode', this.$route.query?.['bankId']);
            this.launchGame2(1, 'MLTP', 'extended', this.$route.query?.['buyIn']);
          } else this.openSignUpDialog();
        },
        immediate: true,
      },

      isRollOrHoldLimitedAvailable: {
        immediate: true,
        deep: true,
        handler(isAvailable) {
          if (
            !isAvailable ||
            (!this.$route.query?.['roll-or-hold-limited-access'] &&
              !this.$route.query?.['buyIn'] &&
              !this.$route.query?.['bankId'])
          )
            return;
          if (this.userSignedIn) {
            this.$store.commit('setPlayerMode', this.$route.query?.['bankId']);
            this.launchGame(2, 'BTG', 'extended', parseInt(this.$route.query?.['buyIn']));
          } else this.openSignUpDialog();
        },
      },

      isEscapeTheCircleLimitedAvailable: {
        immediate: true,
        deep: true,
        handler(isAvailable) {
          if (
            !isAvailable ||
            (!this.$route.query?.['escape-the-circle-limited-access'] &&
              !this.$route.query?.['buyIn'] &&
              !this.$route.query?.['bankId'])
          )
            return;
          if (this.userSignedIn) {
            this.$store.commit('setPlayerMode', this.$route.query?.['bankId']);
            this.launchGame(10, 'BTG', 'extended', parseInt(this.$route.query?.['buyIn']));
          } else this.openSignUpDialog();
        },
      },

      userInfo: {
        immediate: true,
        deep: true,
        handler(value) {
          this.suspendAccount(value);
        },
      },

      isUserSuspended: {
        immediate: true,
        deep: true,
        handler(value) {
          this.suspendAccount(value);
        },
      },
    },

    created() {
      if (this.$route.query.playme && !this.userInfo) {
        this.$store.dispatch('setPartnerId', this.$route.query.playme);
        this.$store.dispatch('changeSignupDialogState', true);
      }

      if (this.$route.query.resetPasswordCode && !this.userInfo) {
        this.$store.dispatch('changeResetPasswordDialogState', {
          value: true,
          resetPasswordCode: this.$route.query.resetPasswordCode,
        });
      }
    },

    async mounted() {
      await this.offsetSecondCarousel();
    },

    beforeDestroy() {
      clearTimeout(this.carouselTimerId);
    },

    methods: {
      async offsetSecondCarousel() {
        this.handleGameTitleCarouselNextClick('gameTitleCarousel2');

        this.carouselTimerId = setTimeout(() => {
          this.handleGameTitleCarouselNextClick('gameTitleCarousel2');
        }, 500);
      },

      openSignUpDialog() {
        this.$store.dispatch('changeSignupDialogState', true);
      },

      async suspendAccount(value) {
        if (!value?.suspended) return;

        EventBus.emit('user-suspended');

        this.$store.dispatch('showAlertDialog', {
          title: 'Your account is suspended',
          text: `
            Your account is currently suspended. Please contact
            <span id='contact-link' class='cursor-pointer gold--text'>Customer Support</span>.
        `,
          permanent: true,
        });

        await this.$nextTick();

        document.getElementById('contact-link').addEventListener('click', () => {
          router.push('/contact');
          this.$store.commit('setAlertDialog', null);
        });
        this.$store.dispatch('setSignupDialogState', false);
      },

      redirectTo(route) {
        this.$router.push(`/${route}`);
      },

      handlePrevSlide(ref) {
        this.$refs[ref]?.swiper?.slidePrev();
      },

      handleNextSlide(ref) {
        this.$refs[ref]?.swiper?.slideNext();
      },

      handleGameTitleCarouselPrevClick(carouselId) {
        const step = this.isTwoCardsBreakpoint ? 2 : 1;
        this.gameTitleLeftmostItem[carouselId] = Math.max(this.gameTitleLeftmostItem[carouselId] - step, 0);
        this.handlePrevSlide(carouselId);
      },

      handleGameTitleCarouselNextClick(carouselId) {
        const step = this.isTwoCardsBreakpoint ? 2 : 1;
        const max = this.getAllGameTitlesCards.length - 1;
        this.gameTitleLeftmostItem[carouselId] = Math.min(this.gameTitleLeftmostItem[carouselId] + step, max);
        this.handleNextSlide(carouselId);
      },

      navigateToUserProfile() {
        this.$router.push('/profileuser/yourprofile');
      },

      openVaultWithPromoPack() {
        const packages = [...this.getPromoPackages].sort((a, b) => b.price - a.price);
        const activePack = packages.find((pack) => pack.label === 'Halloween Pack');

        this.$store.dispatch('setWalletDialog', {
          value: true,
          tab: 'purchase',
          filterPacks: activePack ? [activePack] : null,
        });
      },
    },
  };
</script>

<template>
  <div
    class="hp-background"
    :class="{
      'hp-background--sidebar-open': isSidebarOpened,
      'hp-background--logged-in': isLoggedInUser,
    }"
  >
    <div v-if="isLoading" class="loader-spinner">
      <vue-ellipse-progress
        :data="progdata"
        :empty-color-fill="emptyColorFill"
        :empty-thickness="3"
        :gradient="gradient"
        :legend="false"
        :loading="true"
        :progress="100"
        :size="350"
        :thickness="7"
        animation="rs 700 1000"
        dash="strict 60 0.8"
        empty-color="grey"
        font-size="1.5rem"
        line-mode="out 5"
      >
        <template #legend-caption>
          <v-icon color="#F8BF1E" size="46">mdi-sword-cross</v-icon>
        </template>
      </vue-ellipse-progress>
    </div>
    <template v-else>
      <v-row v-if="isAuthorized" class="text-center hp-spacer">
        <v-col cols="12">
          <div class="welcome-back-message">
            <div v-show="userName">
              Welcome back,
              <span class="break-text" @click="navigateToUserProfile">{{ userName }}</span>
            </div>
          </div>

          <PromoBanners v-if="promoBanners.length" :banners="promoBanners" />

          <div class="category-header">
            <span>Player vs Player</span>
            <div class="action-buttons">
              <v-btn class="action-btn action-btn--arrow" @click="handlePrevSlide('pvpCarousel')">
                <v-icon :size="getCarouselChevronSize">mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn class="action-btn action-btn--arrow" @click="handleNextSlide('pvpCarousel')">
                <v-icon :size="getCarouselChevronSize">mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn class="action-btn" @click="redirectTo('playervsplayer')">View all</v-btn>
            </div>
          </div>
          <swiper-container ref="pvpCarousel" class="game-slider" v-bind="swiperOptions">
            <swiper-slide v-for="(game, index) in getAllPVPGameCards" :key="index">
              <GameCard :on-first-page="index < 2" v-bind="game" @item-clicked="handleGameClick(game)" />
            </swiper-slide>
          </swiper-container>

          <div class="category-header">
            <span>Fish Table Games</span>
            <div v-if="isTwoCardsBreakpoint" class="action-buttons">
              <v-btn class="action-btn action-btn--arrow" @click="handlePrevSlide('cafCarousel')">
                <v-icon :size="getCarouselChevronSize">mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn class="action-btn action-btn--arrow" @click="handleNextSlide('cafCarousel')">
                <v-icon :size="getCarouselChevronSize">mdi-chevron-right</v-icon>
              </v-btn>
              <!--
              <v-btn class="action-btn" @click="redirectTo('challenge-friends')">View all</v-btn>
              -->
            </div>
          </div>
          <swiper-container ref="cafCarousel" class="game-slider" v-bind="{ ...swiperOptions, loop: false }">
            <swiper-slide v-for="(game, index) in getAllFishTableGameCards" :key="index">
              <GameCard :on-first-page="index < 2" v-bind="game" @item-clicked="handleGameClick(game)" />
            </swiper-slide>
          </swiper-container>

          <div v-if="getAllFPGameCards.length" class="category-header">
            <span>FastPlay</span>
            <div class="action-buttons">
              <v-btn class="action-btn action-btn--arrow" @click="handlePrevSlide('fpCarousel')">
                <v-icon :size="getCarouselChevronSize">mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn class="action-btn action-btn--arrow" @click="handleNextSlide('fpCarousel')">
                <v-icon :size="getCarouselChevronSize">mdi-chevron-right</v-icon>
              </v-btn>
              <!--
              <v-btn class="action-btn" @click="redirectTo('fastplay')">View all</v-btn>
              -->
            </div>
          </div>
          <swiper-container ref="fpCarousel" class="game-slider" v-bind="{ ...swiperOptions, loop: false }">
            <swiper-slide v-for="(game, index) in getAllFPGameCards" :key="index">
              <GameCard :on-first-page="index < 2" v-bind="game" @item-clicked="handleGameClick(game)" />
            </swiper-slide>
          </swiper-container>

          <div class="max-rewards-banners category-header">
            <img :src="maxRewardBanner" alt="payments banner" class="my-2" />
          </div>
        </v-col>
      </v-row>
      <template v-else>
        <LoggedOutView />
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  @import 'src/scss/mixins';

  .hp-background {
    container: hp-container / inline-size;
    width: 100%;
    height: 100%;

    @media screen and (max-width: 900px) {
      padding-bottom: 60px;
    }

    @media screen and (min-width: 900px) {
      padding-bottom: 80px;
    }

    &.hp-background--logged-in {
      @media screen and (min-width: 1281px) {
        max-width: calc(100vw - 389px - 64px);
        margin-inline: auto;
        padding-inline: 0;

        .hp-spacer {
          padding-inline: 0;
        }
      }

      @media screen and (min-width: 1842px) {
        max-width: 1448px;
        padding-inline: 32px;

        &:not(.hp-background--sidebar-open) {
          padding-left: 0;
        }
      }
    }
  }

  @container hp-container (min-width: 0px) {
    .hp-spacer,
    .col,
    .row {
      padding: 0;
      margin: 0 !important;
    }

    .hp-spacer {
      padding-inline: $padding-small;
    }
  }

  @container hp-container (min-width: 480px) {
    .hp-spacer {
      padding-inline: $padding-medium;
    }
  }

  @container hp-container (min-width: 768px) {
    .hp-spacer {
      padding-inline: $padding-large;
    }
  }

  @container hp-container (min-width: 1024px) {
    .hp-spacer {
      padding-inline: $padding-xlarge;
    }
  }

  @container hp-container (min-width: 1280px) {
    .hp-spacer {
      padding-inline: $padding-xxlarge;
    }
  }

  @container hp-container (min-width: 1448px) {
    .hp-spacer {
      @media screen and (min-width: 1680px) {
        padding-inline: 0;
      }
    }
  }

  .loader-spinner {
    position: relative;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;

    .ep-container {
      transform: translateY(-25%);
    }
  }

  .break-text {
    word-wrap: break-word;
    word-break: break-all;
    white-space: normal;
  }

  .action-buttons {
    display: flex;
    gap: 10px;
  }

  .v-btn.action-btn {
    min-width: 44px;
    height: 44px;
    border-radius: 10px;
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: none;
    padding: 0 18px;

    &.action-btn--arrow {
      padding: 0;
      opacity: 0.75;

      &:hover {
        opacity: 1;
      }

      &:active {
        opacity: 0.75;
      }
    }

    @media screen and (min-width: 1024px) {
      height: 58px;
      padding: 0 32px;

      &--arrow {
        width: 58px;
      }
    }

    &::v-deep {
      .v-btn__content {
        font-size: 16px;
        font-weight: 500;
        color: #fff;
        text-transform: none;

        @media screen and (min-width: 1024px) {
          font-size: 20px;
        }
      }
    }
  }

  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  .max-rewards-wrapper {
    max-width: 1700px;
  }

  .mbc-banner {
    background-image: url('/images/backgrounds/banner-mbc.png');
    height: 100%;
    width: 100%;
    background-size: cover;

    .mbc-banner__title {
      display: flex;
      justify-content: center;
    }

    .mbc-banner__fog {
      background-image: url('/images/backgrounds/mbc-effects.png');
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
    }

    .mbc-banner__game {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;

      .mbc-logo {
        position: absolute;
        top: 0;
        z-index: 10;

        @media screen and (max-width: 1200px) {
          width: 100px;
        }
      }

      .phone {
        position: absolute;
        top: 0;
        z-index: 2;
        width: 70%;
        background-size: contain;
        background-position: center;

        @media screen and (max-width: 1200px) {
          width: 100%;
        }
      }
    }
  }

  .coins-wrapper {
    margin-top: 75px;

    img {
      background-size: contain;
      width: 97px;
      height: 97px;
      margin: 0 15px;
    }

    @media screen and (max-width: 1200px) {
      img {
        width: 70px;
        height: 70px;
        margin: 0 10px;
      }
    }

    @media screen and (max-width: 1100px) {
      margin-top: 20px;
    }

    @media screen and (max-width: 700px) {
      img {
        width: 50px;
        height: 50px;
        margin: 0 5px;
      }
    }
  }

  .max-rewards-card-image {
    aspect-ratio: 3/2;
  }

  .max-rewards-container {
    .divider-chevron {
      left: 25px !important;
    }
  }

  .rewards-section {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;

    .rewards-card {
      width: 50%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: scale(0.8);

      &::before {
        content: '';
        position: absolute;
        top: 100%;
        width: 100%;
        left: 0;
        background-image: url('/images/cards/max-rewards-card-shadow-right.png');
        height: 50px;
        background-position: top center;
        background-repeat: no-repeat;
        background-size: 100% auto;
        transform-origin: center;
        pointer-events: none;
      }

      &:first-child {
        &::before {
          transform: scale(-1, 1);
        }
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 10px;
        box-shadow: 0 0 10px rgb(0 0 0 / 40%);
      }

      .refer-friends-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        white-space: nowrap;
        text-shadow: 0 0 20px rgb(0 0 0 / 40%);
      }

      .reward-card-text {
        position: absolute;
        top: 45%;
        left: 50%;
        transform: translate(-50%, -50%);

        span {
          font-weight: 800;
          white-space: nowrap;
        }

        span:first-child {
          font-size: 50px;
        }

        span:last-child {
          line-height: 50px;
          font-size: 78px;
        }

        @media screen and (max-width: 1264px) {
          span:first-child {
            font-size: 38px;
          }

          span:last-child {
            font-size: 58px;
          }
        }

        @media screen and (max-width: 959px) {
          span:first-child {
            font-size: 32px;
          }

          span:last-child {
            font-size: 54px;
          }
        }

        @media screen and (max-width: 700px) {
          span:first-child {
            font-size: 26px;
          }

          span:last-child {
            font-size: 42px;
          }
        }

        @media screen and (max-width: 600px) {
          span:first-child {
            font-size: 20px;
          }

          span:last-child {
            font-size: 30px;
            line-height: 25px;
          }
        }

        @media screen and (max-width: 500px) {
          span {
            position: absolute;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          span:first-child {
            top: 45%;
            font-size: 14px;
          }

          span:last-child {
            top: 90%;
            font-size: 20px;
          }
        }

        @media screen and (max-width: 345px) {
          span:first-child {
            font-size: 10px;
          }

          span:last-child {
            top: 80%;
            font-size: 14px;
          }
        }
      }
    }

    @media screen and (max-width: 600px) {
      gap: 40px;

      .rewards-card {
        width: 100%;
        transform: scale(1);
      }
    }
  }

  @keyframes fadeIn {
    from {
      top: 50%;
    }
    to {
      top: 100%;
    }
  }

  .reward-background::v-deep {
    border-radius: 10px;
    box-shadow: 0 0 10px rgb(0 0 0 / 40%);

    @media screen and (min-width: 1900px) {
      max-height: 250px;
    }
  }

  .daily-content-wrapper {
    margin: auto 0;
  }

  .daily-content-bg {
    position: relative;
    top: -5%;

    @media screen and (max-width: 959px) {
      top: 0;
    }
  }

  .daily-content-text {
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);

    span {
      font-weight: 800;
      white-space: nowrap;
    }

    span:first-child {
      font-size: 24px;
    }

    span:last-child {
      line-height: 20px;
      font-size: 54px;
    }

    @media screen and (max-width: 1264px) {
      span:first-child {
        font-size: 18px;
      }

      span:last-child {
        font-size: 30px;
      }
    }

    @media screen and (max-width: 959px) {
      top: 50%;

      span:first-child {
        font-size: 24px;
      }

      span:last-child {
        font-size: 54px;
      }
    }

    @media screen and (max-width: 600px) {
      span:first-child {
        font-size: 18px;
      }

      span:last-child {
        font-size: 40px;
      }
    }

    @media screen and (max-width: 475px) {
      span:last-child {
        font-size: 30px;
      }
    }

    @media screen and (max-width: 400px) {
      span:first-child {
        font-size: 12px;
      }

      span:last-child {
        font-size: 16px;
      }
    }
  }

  .pvp-subtitle {
    line-height: 30px !important;

    @media screen and (max-width: 600px) {
      line-height: 20px !important;
    }
  }

  .badge-new {
    width: 10%;
    position: absolute;
    z-index: 3;
    top: 2%;
    right: -9px;
  }

  .divider-text {
    color: #2e213c;
  }

  .mr-popup-banner::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: rgb(58, 43, 74);
    background: linear-gradient(
      0deg,
      rgba(58, 43, 74, 1) 20%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  .mr-popup-content {
    background: rgba(58, 43, 74, 1);
  }

  .header-container {
    max-width: 1750px;
    gap: 16px;
    padding: 26px;

    @media screen and (max-width: 599px) {
      flex-direction: column !important;
    }

    .daily-coins-container {
      flex-shrink: 0;

      @media screen and (max-width: 599px) {
        width: 100% !important;
      }

      .daily-banner {
        height: 100%;
      }
    }

    .daily-coins-container {
      position: relative;
      border-radius: 15px;
      max-width: 420px;

      @media screen and (min-width: 600px) {
        max-width: 500px;
      }

      .daily-button {
        position: absolute;
        bottom: 55px;
        left: 50%;
        transform: translate(-50%);
      }

      .daily-banner-bgr {
        border-radius: 15px;

        &:hover {
          border-radius: 15px;
          box-shadow: 0 0 23px 1px rgb(255, 202, 19);
        }

        &::v-deep .v-responsive__content {
          display: flex;
          align-items: flex-end;
          justify-content: center;

          .cta-button {
            margin-bottom: 24px;

            @media screen and (min-width: 601px) {
              margin: 0 6px 30px;
            }

            @media screen and (min-width: 768px) {
              margin: 0 auto 30px;
            }

            @media screen and (min-width: 1024px) {
              margin-bottom: 42px;
            }

            @media screen and (min-width: 1440px) {
              margin-bottom: 60px;
            }
          }
        }
      }
    }
  }

  .PVP-container {
    padding-top: 0;

    .pvp-logo-wrapper {
      position: relative;
      width: 100%;
      height: 180px;
      display: flex;
      justify-content: center;

      .pvp-logo {
        pointer-events: none !important;
        top: -60px;
        max-width: 500px;
        position: absolute;
      }
    }

    .pvp-section {
      position: relative;

      .pvp-cards-wrapper {
        max-width: 80%;
        margin: 0 auto !important;
        position: relative;

        .pvp-card {
          position: relative;
          background: #3a2350;
          border: 3px solid white;
          border-radius: 8px;
          box-shadow: 0 0 10px 1px white;
          min-width: 100%;

          .pvp-card-icon {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }

          .pvp-card-title {
            padding-top: 100px;
          }
        }

        .pvp-card-col {
          .pvp-card-icon {
            padding-top: 20px;
          }

          &:nth-of-type(2) {
            .pvp-card-icon {
              padding-top: 25px;
            }
          }

          &:last-of-type {
            .pvp-card-icon {
              padding-top: 5px;
            }
          }
        }
      }
    }

    .carousel-container {
      @media screen and (max-width: 600px) {
        width: 100% !important;

        .hp-carousel {
          width: 100%; /* Make the carousel take up the full width */
          height: auto; /* Adjust height automatically based on the content */
          aspect-ratio: 16 / 9;
          max-width: 420px !important;
          margin: 0 auto;
          max-height: 250px;
        }
      }

      display: flex;
      flex-direction: row;
      justify-content: flex-end;

      .carousel-wrapper::v-deep {
        width: 100%;
        max-height: 500px;

        @media screen and (max-width: 600px) {
          .carouse-banner-image-1 {
            .v-image {
              .v-image__image {
                background-image: url('/images/banners/mob-hp-lo-carousel-banner-1.png') !important;
              }
            }
          }

          .carouse-banner-image-2 {
            .v-image {
              .v-image__image {
                background-image: url('/images/banners/mob-hp-lo-carousel-banner-2.png') !important;
              }
            }
          }

          .carouse-banner-image-3 {
            .v-image {
              .v-image__image {
                background-image: url('/images/banners/mob-hp-lo-carousel-banner-4.png') !important;
              }
            }
          }

          .carouse-banner-image-4 {
            .v-image {
              .v-image__image {
                background-image: url('/images/banners/mob-hp-lo-carousel-banner-3.png') !important;
              }
            }
          }
        }

        .v-responsive__content {
          width: 100% !important;
        }
      }

      .hp-carousel {
        height: 100% !important;
        text-shadow: 2px 2px 10px black;

        .sx-slider-img {
          margin-top: 25px;
          max-width: 400px;
          margin-left: -20px;
        }

        .mbc_img {
          position: absolute;
          max-width: 600px;
          background-size: contain;
          left: 52%;
          transform: translateX(-50%);
          z-index: -1;
        }

        .button-container {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translate(-50%, 0);
          padding-bottom: 55px;
          width: 100%;
          display: flex;
          justify-content: center;
          gap: 30px;
        }
      }
    }

    .right-banners-container {
      text-shadow: 2px 2px 10px black;
      display: flex;
      flex-direction: column;
      gap: 20px;

      .banner {
        position: relative;
        height: 265px;

        .new-badge {
          position: absolute;
          width: 120px;
          top: -30px;
          left: -6px;
        }

        .banner-sx-logo {
          position: absolute;
          width: 250px;
          top: 45%;
          left: 50%;
          transform: translate(-50%, 0);
          z-index: 2;
        }

        .banner-img {
          text-shadow: 2px 2px 5px black;
          height: 100%;
          width: 100%;
          object-fit: cover;
          object-position: 50% 10%;
        }
      }
    }
  }

  .hp-carousel,
  .banner {
    transition: all 0.1s linear;

    &:hover {
      border-radius: 15px;
      box-shadow: 0 0 23px 1px rgb(255, 202, 19);
    }
  }

  .hp-carousel,
  .banner,
  .banner-img {
    border-radius: 15px;

    .v-carousel__item {
      border-radius: 15px;
    }
  }

  .hp-carousel::v-deep {
    .v-image {
      height: 100% !important;
    }

    .v-carousel__controls {
      transform: scale(0.6) translateY(5px);
      background: none !important;
      height: 40px;

      @media screen and (min-width: 1024px) {
        transform: scale(0.9);
      }

      .v-btn {
        margin: 5px;
        width: 15px;
        height: 15px;
      }

      .v-icon {
        font-size: 15px !important;
        width: 15px;
        height: 15px;
        border-radius: 100px;
        border: 1px solid white;
        background-color: transparent;
        transition: all 400ms ease;
        cursor: pointer;
        color: transparent;
      }

      .v-btn.v-btn--active .v-icon {
        color: white;
        opacity: 1;
      }
    }

    .v-responsive__content {
      display: flex;
      align-items: flex-end;
      justify-content: center;

      .cta-button {
        margin-bottom: 30px;

        @media screen and (min-width: 1024px) {
          margin-bottom: 42px;
        }

        @media screen and (min-width: 1440px) {
          margin-bottom: 60px;
        }
      }
    }
  }

  .worlds-slider {
    max-width: 80%;
    margin: auto;

    .next-button,
    .prev-button {
      top: 40% !important;
      width: 50px !important;
    }

    .next-button {
      right: -10% !important;
    }

    .prev-button {
      left: -10% !important;
    }
  }

  .games-slider {
    max-width: 90%;
    margin: auto;
  }

  .close-button {
    position: absolute;
    top: 12px;
    right: 8px;
    font-size: 32px !important;
    color: white !important;
    text-shadow: 0 0 4px #000;
  }

  .rewards-slider .glide__slide::before {
    content: '';
    position: absolute;
    top: 100%;
    width: 100%;
    left: 0;
    background-image: url('/images/cards/max-rewards-card-shadow-right.png');
    height: 100%;
    background-position: top center;
    background-repeat: no-repeat;
    background-size: 100% auto;
    transform-origin: center;
  }

  .glide__slides .glide__slide.glide__slide--active::before {
    transform: scale(-1, 1);
  }

  .games-slider .glide__slide {
    position: relative;
    pointer-events: none;

    .button-container {
      pointer-events: none;

      button {
        pointer-events: auto;
      }
    }
  }

  .games-slider .games-slide-active-content::before {
    content: '';
    position: absolute;
    top: 100%;
    width: 100%;
    left: 0;
    background-image: url('/images/cards/PLAY SOLO Divider Shadow.png');
    height: 100%;
    background-position: top center;
    background-repeat: no-repeat;
    background-size: 100% auto;
  }

  .games-slider button[data-glide-dir='<'] {
    left: -4% !important;
  }

  .games-slider button[data-glide-dir='>'] {
    right: -4% !important;
  }

  .games-slider button[data-glide-dir='<'],
  .games-slider button[data-glide-dir='>'] {
    top: 50%;
    width: 10% !important;
  }

  .games-slider .glide__slides {
    padding-top: 40px;
    padding-bottom: 40px;
  }

  .worlds-slider .glide__slides {
    padding-bottom: 0;
  }

  .divider-play-vs-friends {
    position: relative;
    width: 100%;
    height: 70px;
    background: linear-gradient(90deg, var(--v-main-lighter-2-base) 16%, rgba(255, 255, 255, 1) 100%);

    .divider-chevron {
      position: absolute;
      left: 2%;
      width: 100px;
      height: 100%;

      .arrow {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: #5d3f7cff;
        clip-path: polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%);
        z-index: 2;
      }

      .arrow1 {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: white;
        clip-path: polygon(35% 0%, 60% 50%, 35% 100%, 0% 100%, 25% 50%, 0% 0%);
        z-index: 3;
        left: 30%;
      }

      .arrow2 {
        left: 50%;
        width: 100%;
        height: 100%;
        position: absolute;
        background-color: white;
        clip-path: polygon(35% 0%, 60% 50%, 35% 100%, 0% 100%, 25% 50%, 0% 0%);
        z-index: 1;
      }
    }

    &:before {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 400px;
      box-shadow: 0 26px 11px 0 inset rgba(0, 0, 0, 0.1);
    }
  }

  .divider-max-rewards {
    position: relative;
    width: 100%;
    height: 70px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, var(--v-main-lighter-2-base) 100%);

    .divider-chevron {
      position: absolute;
      right: 2%;
      width: 100px;
      height: 100%;
      transform: rotate(180deg);

      .arrow {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: #7f6eac;
        clip-path: polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%);
        z-index: 2;
      }

      .arrow1 {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: white;
        clip-path: polygon(35% 0%, 60% 50%, 35% 100%, 0% 100%, 25% 50%, 0% 0%);
        z-index: 3;
        left: 30%;
      }

      .arrow2 {
        left: 50%;
        width: 100%;
        height: 100%;
        position: absolute;
        background-color: white;
        clip-path: polygon(35% 0%, 60% 50%, 35% 100%, 0% 100%, 25% 50%, 0% 0%);
        z-index: 1;
      }
    }

    &:before {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 400px;
      box-shadow: 0 30px 10px 0 inset rgba(0, 0, 0, 0.1);
    }
  }

  .play-solo-mobile-card::v-deep {
    position: relative;
    overflow: visible;

    .v-image__image {
      border-radius: 10px !important;
    }

    .mobile-logo {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &:before {
      content: '';
      position: absolute;
      top: 95%;
      left: 0;
      width: 100%;
      height: 10%;
      background-image: url('/images/cards/solo_cards_shadow.png');
      background-repeat: no-repeat;
      background-size: 100% auto;
      background-position: top center;
    }
  }

  @media screen and (max-width: 1264px) {
    .mbc_img {
      max-width: 500px !important;
    }

    .worlds-slider {
      .worlds-slide {
        .slider-logo {
          position: absolute;
          z-index: 1;
          bottom: 0;
          width: 80%;
          left: 50%;
          transform: translate(-50%, 10%);
        }
      }
    }
  }

  @media screen and (max-width: 959px) {
    .hp-carousel {
      .button-container {
        padding-bottom: 35px !important;
      }
    }

    .pvp-cards-wrapper {
      max-width: 100% !important;
    }

    .sx-slider-img {
      margin-top: 25px;
      max-width: 300px !important;
      margin-left: -20px;
    }

    .daily-coins-container {
      .daily-button {
        bottom: 0 !important;
      }

      .daily-banner {
        .daily-divider {
          width: 30% !important;
          height: 2px !important;
        }

        .digit {
          max-width: 65px !important;
        }

        .number {
          max-width: 150px !important;
        }

        .plus-icon {
          font-size: 40px !important;
        }

        .number {
          max-width: 120px;
        }
      }
    }

    .badge-new {
      right: 20px;
    }

    .v-window__container {
      height: 250px !important;
    }

    .new-badge {
      width: 100px !important;
      top: -10px !important;
    }

    .v-carousel__controls {
      background: none !important;
      height: 25px;
    }

    .carousel-container {
      justify-content: center !important;

      .mbc-bottom-text {
        display: none;
      }

      .hp-carousel {
        .mbc_img {
          max-width: 180px !important;
        }

        .button-container {
          gap: 20px !important;
        }
      }
    }

    .right-banners-container {
      max-width: 100% !important;
      display: flex;
      flex-direction: row !important;
      justify-content: center;
      width: 100%;

      .banner {
        width: 100%;
        height: 200px !important;

        .banner-caf-text {
          bottom: 7% !important;
        }
      }
    }

    .reward-card-ribbon {
      position: absolute;
      top: -10%;
      right: -0.9%;
      z-index: 1;
      width: 150px;
    }

    .reward-card-ribbon-mobile {
      position: absolute;
      left: -6px;
      top: -6%;
      z-index: 1;
      width: 100px;
    }
  }

  @media screen and (max-width: 460px) {
    .PVP-container .right-banners-container .banner {
      height: 200px !important;

      .banner-caf-logo {
        top: 40%;
      }
    }

    .carousel-container {
      .hp-carousel {
        .button-container {
          gap: 15px !important;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .max-rewards-bg {
      .divider-play-vs-friends {
        margin-bottom: 50px !important;
      }

      .max-rewards-container {
        &:after {
          width: 70px !important;
        }
      }
    }

    .pvp-logo-wrapper {
      height: 50px !important;

      .pvp-logo {
        top: -70px !important;
        max-width: 320px !important;
      }
    }

    .badge-new {
      width: 20%;
    }

    .refer-friends-card::v-deep {
      .v-image__image {
        background-image: url('/images/banners/ReferFriendsMobile.png') !important;
      }
    }

    .divider-bg .v-image__image {
      background-position: left center !important;
    }
    .divider-bg-right .v-image__image {
      background-position: right center !important;
    }

    .games-slider .glide__slides {
      padding-top: 30px;
      padding-bottom: 30px;
    }

    .games-slider button[data-glide-dir='<'],
    .games-slider button[data-glide-dir='>'] {
      top: 50%;
      width: 10% !important;
    }

    .games-slider button[data-glide-dir='<'] {
      left: -5% !important;
    }

    .games-slider button[data-glide-dir='>'] {
      right: -5% !important;
    }

    .games-slider .games-slide-active-content::before {
      position: absolute;
      left: 20%;
      margin-top: 17px;
      width: 60%;
      background-image: url('/images/cards/button-shadow.png');
    }

    .play-btn-shadow {
      width: 80%;
      margin: 0 auto;
      height: 2px;
      box-shadow: 2px 2px 10px;
    }

    .games-slider-container {
      padding-bottom: 80px;
    }

    .game-description-text {
      position: absolute;
      text-align: left;
      margin-top: 10px;
      padding: 0 25px;
    }
  }

  .divider-bg,
  .divider-bg-right {
    position: relative;
    overflow: visible;
  }

  .divider-wrapper {
    width: 100%;
    position: relative;

    .divider-play-solo {
      height: 70px;
      position: relative;
      background-color: white;
      width: 100%;
      z-index: 2;
    }

    &:after {
      z-index: 1;
      content: '';
      position: absolute;
      left: 0;
      width: 100%;
      top: 0;
      height: 100%;
      box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.2);
    }
  }

  .divider-bg-right::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 100%;
    box-shadow: 0 70px 10px 0 inset rgba(0, 0, 0, 0.5);
  }

  .glow-text {
    text-shadow:
      0 0 1px white,
      0 0 1px white,
      0 0 10px white,
      0 0 10px white,
      0 0 120px white;
    font-size: 26px !important;
    font-weight: 600 !important;
  }

  .glow-text .text-h2 {
    font-size: 55px !important;
    font-weight: 600 !important;
  }

  .max-coins-img {
    width: 500px;
    max-width: 95%;
    margin: auto;
  }

  .btg-logo-img {
    height: auto;
    max-width: 700px;
    margin: auto;
  }

  .mainBanner {
    width: 100%;
  }

  .mainBanner-logo {
    position: absolute;
    top: 55%;
    width: 100%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 750px;
  }

  .reward-card-logo {
    position: absolute;
    width: 20%;
    left: 2%;
    top: 8%;
  }

  .reward-card-ribbon {
    position: absolute;
    top: -10%;
    left: -1.2%;
    z-index: 1;
    width: 20%;
  }

  .reward-card-logo.small {
    width: 10%;
  }

  .referfriends_text_card {
    color: white;
    position: relative;
    font-size: 62px;
    text-align: center;
    top: 40%;
    left: 50%;
    font-weight: 900;
    transform: translate(-50%, -50%);
  }

  .preview-video {
    height: 100%;
    border-radius: 20px;
    border: 3px solid #f8bf1e;
  }

  .video-ribbon {
    position: absolute;
    top: -3%;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
  }

  .video-ribbon img {
    position: absolute;
    top: 0;
    left: 0;
  }

  .carousel-3d-container figure {
    margin: 0;
  }

  .carousel-3d-container {
    .carousel-3d-slide {
      background-color: rgba(0, 0, 0, 0.5);
      margin: 10px;
    }
  }

  .carousel-3d-container figcaption {
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    position: absolute;
    bottom: 0;
    padding: 15px;
    font-size: 12px;
    min-width: 100%;
    box-sizing: border-box;
  }

  .carousel-3d-container buttonarea {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    bottom: 0;
    padding: 15px;
    font-size: 12px;
    min-width: 100%;
    box-sizing: border-box;
  }

  .pvp-section {
    position: relative;
    width: 100%;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
  }

  .logged_pvp-lg {
    padding: 0;
    position: relative;
    width: 100%;
    height: 100%;
    max-height: 250px;
    background-color: #5f407c;
  }

  .hp_logged_in_content {
    position: relative;
    width: 100%;
    background: rgb(78, 50, 116);
    background: linear-gradient(
      0deg,
      rgb(78, 50, 116) 0%,
      rgb(94, 63, 123) 25%,
      rgb(135, 120, 182) 50%,
      rgb(94, 63, 123) 75%,
      rgb(78, 50, 116) 100%
    );
  }

  .solo-games {
    .solo-card-container {
      max-width: 480px;
    }

    .mobile-logo {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    a {
      bottom: 30px;
      position: relative;
    }
  }

  .solo-games button {
    margin-bottom: 7% !important;
  }

  .solo-game-card {
    cursor: pointer;
    border: 1px solid var(--v-main-lighter-3-base);
    border-radius: 12px;
    box-shadow: 1px 35px 9px -26px rgba(76, 68, 100, 0.91);
    -webkit-box-shadow: 1px 35px 9px -26px rgba(76, 68, 100, 0.91);
    -moz-box-shadow: 1px 35px 9px -26px rgba(76, 68, 100, 0.91);

    &:hover {
      box-shadow: 0 0 12px var(--v-gold-base);
    }
  }

  .max-rewards-bg {
    position: relative;
    background-repeat: repeat;
    background-size: cover;
    background-position: center center;

    .max-rewards-cards-wrapper {
      max-width: 100%;
    }

    .max-rewards-container {
      width: 100%;
      flex-direction: column;
      position: relative;

      .divider-play-vs-friends {
        margin-bottom: 100px;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        right: 24px;
        width: 155px;
        height: 285px;
        z-index: 4;
        background-image: url('/images/figures/girl-pointing.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: bottom right;
      }
    }

    .refer-friends-card {
      border-radius: 10px;
      box-shadow: 0 0 10px rgb(0 0 0 / 40%);
    }

    .max-rewards-left-card-text {
      text-shadow: 0 0 11px rgba(255, 202, 20, 0.81);
    }

    .maxcoins-text {
      position: absolute;
      width: 90%;
      top: 35%;
      left: 50%;
      transform: translate(-50%, -50%);
      object-position: center center;
    }
  }

  .detailbg {
    position: relative;
    background: linear-gradient(180deg, rgba(62, 21, 93, 1) 0%, rgba(135, 120, 182, 1) 52%);

    .worlds-text {
      width: 90%;
    }
  }

  .pvp_text {
    left: 0;
    top: 130px;
    text-shadow: 1px 1px 1px #000000;
    text-align: center;
    font-style: normal;
    font-weight: 900;
    font-size: 32px;
    line-height: 110%;
    color: white;
  }

  .rewards_text {
    left: 0;
    top: 130px;
    text-shadow: 1px 1px 1px #000000;
    text-align: center;
    font-style: normal;
    font-weight: 900;
    font-size: 32px;
    line-height: 110%;
    color: white;
  }

  .ffcard_detail {
    position: absolute;
    left: 0;
    top: 8%;
  }

  .ff_text_detail {
    color: white;
    position: relative;
    font-size: 32px;
    text-align: left;
    top: 120px;
    left: 0;
    font-weight: 600;
  }

  .loggedin_banner_detail {
    color: white;
    position: relative;
    font-size: 32px;
    width: 60%;
    text-align: left;
    top: 50px;
    left: 0;
    font-weight: 400;
  }

  .detailbglight {
    position: relative;
    background-color: #242860;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
  }

  .battle-video {
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
    box-shadow: 0 0 10px rgb(0 0 0 / 50%);
  }

  .ff-logo-last-time {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
  }

  .btg-logo-mobile {
    margin-top: -20%;
  }

  .worlds-slide {
    padding: 0 20px 0 5px;

    .slider-image {
      position: relative;
      max-width: 550px;
      width: 100%;
      margin: 0 auto;
      border: 7px solid #6d5e82;
      border-radius: 20px;
    }

    .slider-logo {
      position: absolute;
      z-index: 1;
      bottom: 0;
      width: 80%;
      left: 50%;
      transform: translate(-50%, 10%);
    }
  }

  .worlds-slide.glide__slide {
    padding: 30px 20px;

    :hover {
      .slider-image {
        box-shadow: 0 0 23px -4px rgba(255, 202, 19, 1);
        border: 7px solid #191919;
      }
    }
  }

  .image-wrapper {
    position: relative;
  }

  .worlds-slide .image-wrapper .logo-img {
    position: absolute;
    bottom: 20px;
    width: 40%;
    left: 20px;
    border-radius: 0;
  }

  .worlds-slide .description-wrapper {
    text-align: center;
    transition: width 0.5s ease;
    padding: 0 5px;
    width: 100%;
  }

  .worlds-slide .description-wrapper > div {
    min-height: 80px;
  }

  .games-slide-active-content {
    position: relative;
    width: 78%;
  }

  .video-preview-frame {
    position: absolute;
    left: 4%;
    top: 50%;
    transform: translateY(-50%);
    width: 45%;
    text-align: center;
    text-align: -webkit-center;
    overflow: hidden;
    height: 90%;
    display: grid;
    align-content: center;
  }

  .video-preview-frame-wrapper {
    background: #313131;
    border-radius: 20px;
    overflow: hidden;
    height: 100%;
  }

  .games-slide-active-content .game-description {
    position: absolute;
    left: 50%;
    top: -7%;
    width: 50%;
    padding: 0 10px;
    height: 90%;
    align-content: center;
  }

  .games-slide-active-content .game-logo {
    max-height: 90px;
  }

  .next-slide-bg-1 {
    position: relative;
    background-image: url('/images/sliders/games/NextCard_MA-min.png');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right center;
    width: 22%;
    place-items: flex-end;
  }

  .next-slide-bg-2 {
    position: relative;
    background-image: url('/images/sliders/games/NextCard_MBC-min.png');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right center;
    width: 22%;
    place-items: flex-end;
  }

  .next-slide-bg-3 {
    position: relative;
    background-image: url('/images/sliders/games/NextCard_SX-min.png');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right center;
    width: 22%;
    place-items: flex-end;
  }

  .next-slide-bg-4 {
    position: relative;
    background-image: url('/images/sliders/games/NextCard_DS-min.png');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right center;
    width: 22%;
    place-items: flex-end;
  }

  @media (min-width: 600px) and (max-width: 960px) {
    .pvp-logo-wrapper {
      height: 100px !important;

      .pvp-logo {
        top: -70px !important;
        max-width: 400px !important;
      }
    }

    .max-rewards-bg {
      .divider-play-vs-friends {
        margin-bottom: 50px !important;
      }

      .max-rewards-container {
        &:after {
          width: 90px !important;
        }
      }
    }
  }

  @media (max-width: 959px) {
    .divider-play-vs-friends,
    .divider-max-rewards,
    .divider-play-solo {
      height: 50px !important;

      .divider-chevron {
        width: 50px;
      }
    }

    .reward-card-ribbon {
      position: absolute;
      top: -10%;
      right: -0.9%;
      z-index: 1;
      width: 150px;
    }

    .worlds-slide .v-btn {
      max-width: 100%;
      min-width: auto !important;
    }

    .reward-container {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }
  }

  @media screen and (max-width: 992px) {
    .mainBanner-logo {
      width: 90%;
      margin-top: 25px;
    }
  }

  @media (max-width: 600px) {
    .sx-slider-img {
      margin-top: 25px;
      max-width: 200px !important;
      margin-left: -20px;
    }

    .sx-slider-text {
      margin-left: -5px !important;
      max-width: 190px !important;
    }

    .reward-card-ribbon {
      position: absolute;
      top: -10%;
      right: 1.5%;
      z-index: 1;
      width: 100px;
    }

    .reward-card-ribbon-mobile {
      width: 75px;
    }

    .logged_pvp-lg {
      .cf-logo {
        width: 100%;
      }
    }

    .worlds-slide .description-wrapper {
      text-align: center;
      padding: 0 5px;
    }

    .worlds-slide.glide__slide--active .description-wrapper {
      text-align: center;
    }
  }

  @media (max-width: 750px) {
    .games-slide-active-content .game-description {
      top: -3%;
    }

    .games-slide-active-content .game-logo {
      max-height: 65px;
      padding: 10px;
    }
  }

  @media (min-width: 600px) and (max-width: 910px) {
    .rewards-row button {
      min-width: auto !important;
      width: 100%;
    }
  }

  @media (max-width: 600px) {
    .divider-bg .v-image__image {
      background-position: left center;
    }

    .v-carousel__controls {
      background: none !important;
      height: 41px;
    }

    .video-ribbon {
      top: -7%;
    }

    .video-preview-frame {
      overflow: visible;
      flex-direction: column;
      left: 50%;
      transform: translate(-50%, -50%);
      top: 50%;
      width: 85%;

      .video-play-btn {
        pointer-events: auto;
        z-index: 2;
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        bottom: -30px;
      }
    }

    .next-slide-bg-1 {
      background-image: url('/images/sliders/games/NextCard_MA_Mobile-min.png');
      width: 40%;
    }
    .next-slide-bg-2 {
      background-image: url('/images/sliders/games/NextCard_MBC_Mobile-min.png');
      width: 40%;
    }
    .next-slide-bg-3 {
      background-image: url('/images/sliders/games/NextCard_SX_Mobile-min.png');
      width: 40%;
    }
    .next-slide-bg-4 {
      background-image: url('/images/sliders/games/NextCard_DS_Mobile-min.png');
      width: 40%;
    }

    .games-slide-active-content {
      width: 70%;
    }

    .games-slide-active-content .game-logo {
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 0;
      max-height: 80%;
      top: 45%;
    }

    .purple-background {
      width: 80%;
    }
  }

  @media (max-width: 600px) {
    .worlds-slide {
      padding: 20px !important;

      .slider-logo {
        width: 60% !important;
        transform: translate(-50%, 5%) !important;
      }

      .slider-image {
        max-width: 70%;
        margin: 0 auto;
      }
    }

    .worlds-slide .description-wrapper,
    .worlds-slide.glide__slide--active .description-wrapper {
      text-align: center;
    }
  }

  @media (max-width: 400px) {
    .worlds-slider {
      .worlds-slide {
        padding: 24px 4px !important;
      }
    }

    .divider-play-vs-friends,
    .divider-play-solo,
    .divider-max-rewards {
      height: 35px !important;

      .divider-chevron {
        width: 35px;

        .arrow {
          background-color: #6c5797;
        }
      }
    }

    .reward-card-ribbon-mobile {
      width: 60px;
    }
  }

  .welcome-back-message {
    min-height: 12px;
    margin-top: 24px;
    text-align: left;
    font-size: 28px;
    font-weight: 700;
    color: #e7e5ec;
    position: relative;
    z-index: 1;

    span {
      cursor: pointer;
      color: #f8bf1e;
      text-decoration: underline;
    }

    @media screen and (min-width: 480px) {
      margin-top: 32px;
    }

    @media screen and (min-width: 1024px) {
      font-size: 24px;
    }
  }

  .category-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
    margin-bottom: 16px;

    @media screen and (min-width: 1024px) {
      margin-top: 40px;
    }

    span {
      font-size: 18px;
      font-weight: 600;
      color: #e7e5ec;

      @media screen and (min-width: 1024px) {
        font-size: 24px;
      }
    }
  }

  .promo-banners {
    margin-top: -32px;
  }

  .daily-banner {
    position: relative;

    &::v-deep .v-responsive__content {
      display: flex;
      justify-content: center;

      .join-button {
        position: absolute;
        bottom: 20px;

        @media screen and (min-width: 600px) {
          min-width: auto;
          min-height: auto;
          width: calc(100% - 50px);
          height: clamp(16px, 2.6vw, 38px);
          bottom: 6px;

          span {
            font-size: clamp(12px, 1.7vw, 20px);
          }
        }

        @media screen and (min-width: 720px) {
          bottom: clamp(12px, 3vw, 68px);
        }

        @media screen and (min-width: 720px) {
          width: auto;
        }
      }
    }
  }

  .max-rewards-banners {
    margin-top: 80px;
    max-width: calc(100vw - 80px);
    margin-inline: auto;

    @media screen and (min-width: 1280px) {
      max-width: 1280px;
    }

    img {
      max-width: 100%;
      margin-inline: auto;
    }
  }
</style>
